"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { Facebook, Twitter, Instagram, Mail } from "lucide-react";

/**
 * Footer — FULL file
 *
 * Behavior:
 * - Measures its position relative to the viewport.
 * - If any part of the footer is below the viewport bottom, applies a small negative translateY
 *   to move it up just enough to be fully visible (plus a small 16px breathing room).
 * - Recalculates on resize & scroll.
 * - Respects prefers-reduced-motion (no animated shifts).
 *
 * Usage:
 * Replace your existing Footer with this file.
 */

function MiniFlame({ size = 12 }: { size?: number }) {
  const uid = useMemo(() => `f-${Math.round(Math.random() * 1e6)}`, []);
  const w = Math.max(10, size);
  const h = Math.max(14, Math.round(size * 1.4));
  const css = `
    @keyframes ${uid}-f { 0% { transform: translateY(0); opacity: 0.9 } 40% { transform: translateY(-6%); opacity: 1 } 80% { transform: translateY(0); opacity: 0.85 } 100% { transform: translateY(0); opacity: 0.9 } }
    .${uid} { display:inline-block; width:${w}px; height:${h}px; position:relative; pointer-events:none; vertical-align:middle; }
    .${uid} .l { position:absolute; left:50%; transform:translateX(-50%); bottom:0; border-radius:50% 40% 20% 40% / 70% 60% 50% 40%; }
    .${uid} .a { width:${Math.round(w * 0.86)}px; height:${Math.round(h * 0.9)}px; background: radial-gradient(circle at 35% 24%, rgba(255,240,140,1), rgba(255,140,40,0.08) 48%, transparent 72%); animation:${uid}-f ${700 + Math.round(Math.random() * 300)}ms infinite ease-in-out; opacity:0.98; }
    .${uid} .b { width:${Math.round(w * 0.56)}px; height:${Math.round(h * 0.66)}px; background: radial-gradient(circle at 50% 18%, rgba(255,255,255,0.96), rgba(255,220,70,0.06) 44%, transparent 72%); animation:${uid}-f ${820 + Math.round(Math.random() * 260)}ms infinite ease-in-out; opacity:0.9; }
  `;
  return (
    <span className={uid} aria-hidden style={{ display: "inline-block", verticalAlign: "middle" }}>
      <style>{css}</style>
      <span className="l a" />
      <span className="l b" />
    </span>
  );
}

export default function Footer(): JSX.Element {
  const year = new Date().getFullYear();
  const ref = useRef<HTMLElement | null>(null);

  // state: px to translate upward (negative value moves footer up)
  const [liftPx, setLiftPx] = useState(0);

  // reduced motion
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && "matchMedia" in window) {
      const mm = window.matchMedia("(prefers-reduced-motion: reduce)");
      setReducedMotion(mm.matches);
      const handler = () => setReducedMotion(mm.matches);
      mm.addEventListener?.("change", handler);
      return () => mm.removeEventListener?.("change", handler);
    }
    return;
  }, []);

  // measure & adjust lift
  useEffect(() => {
    if (typeof window === "undefined") return;
    let rafId = 0;

    const measureAndAdjust = () => {
      rafId = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return setLiftPx(0);

        const rect = el.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        // when bottom > viewportHeight, some part is cut off
        const overflow = rect.bottom - viewportHeight;

        if (overflow > 0) {
          // calculate lift: bring footer up by overflow + padding (16px)
          // clamp to at most 70% of footer height so we don't pull it too far
          const desired = -(overflow + 16);
          const maxLift = -(rect.height * 0.7);
          const clamped = Math.max(maxLift, desired);
          setLiftPx(clamped);
        } else {
          // if it's fully visible, clear any previous lift
          setLiftPx(0);
        }
      });
    };

    // initial measurement
    measureAndAdjust();

    // adjust on resize and scroll
    const onResize = () => measureAndAdjust();
    const onScroll = () => measureAndAdjust();

    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // motion props for entrance
  const motionProps = reducedMotion
    ? { initial: false as const, whileInView: { opacity: 1, y: 0 } as any, transition: { duration: 0 } }
    : { initial: { opacity: 0, y: 8 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.45, ease: "easeOut" } };

  // prepare inline transform style
  const transformStyle = liftPx === 0 ? undefined : { transform: `translateY(${liftPx}px)` };

  return (
    <footer
      ref={ref}
      className="sc-footer"
      aria-labelledby="footer-heading"
      style={{
        // ensure footer is in normal flow and not clipped by positioning
        position: "relative",
        // apply lift if needed (animated via CSS if reducedMotion=false)
        ...transformStyle,
      }}
    >
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;700;800&family=Inter:wght@300;400;600;700;800&display=swap');

        :root {
          --gold: #FFD64D;
          --gold-rgb: 255,214,77;
          --dark: #0b0b0d;
          --white: #ffffff;
          --max-width: 1180px;
        }

        .sc-footer {
          width: 100%;
          box-sizing: border-box;
          overflow-x: hidden;
          font-family: "Inter", "Outfit", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
          -webkit-font-smoothing: antialiased;
          /* keep a subtle baseline so footer doesn't feel pushed off the page */
        }

        /* Contained golden background (glow is inside the footer box) */
        .footer-bg {
          width: 100%;
          box-sizing: border-box;
          background:
            radial-gradient(480px 160px at 50% 6%, rgba(var(--gold-rgb),0.96), rgba(var(--gold-rgb),0.34) 26%, rgba(var(--gold-rgb),0.12) 44%, transparent 68%),
            linear-gradient(180deg, rgba(255,250,230,0.07), rgba(255,245,220,0.03));
          padding: clamp(14px, 3.2vw, 28px) clamp(12px, 3.0vw, 28px) clamp(16px, 3.5vw, 36px);
          border-top: 1px solid rgba(0,0,0,0.04);
        }

        .sc-inner {
          max-width: var(--max-width);
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr minmax(140px, 260px) minmax(140px, 260px);
          gap: clamp(12px, 2.4vw, 22px);
          align-items: start;
        }

        @media (max-width: 980px) {
          .sc-inner { grid-template-columns: 1fr minmax(140px, 240px); }
        }
        @media (max-width: 660px) {
          .sc-inner { grid-template-columns: 1fr; gap: 12px; }
        }

        .panel { min-width: 0; box-sizing: border-box; }

        .brand {
          background: linear-gradient(180deg, rgba(6,6,6,0.96), rgba(12,12,12,0.82));
          color: var(--white);
          border-radius: 12px;
          padding: 12px;
          display:flex;
          flex-direction:column;
          gap: 8px;
          box-shadow: 0 14px 40px rgba(0,0,0,0.45);
          border: 1px solid rgba(255,255,255,0.03);
        }

        .brand-top { display:flex; gap:12px; align-items:center; min-width:0; }

        .logo-tile {
          width: 72px;
          height: 72px;
          border-radius: 12px;
          background: linear-gradient(180deg, #040404, #0b0b0b);
          display:grid;
          place-items:center;
          position: relative;
          flex-shrink: 0;
          box-shadow: 0 10px 28px rgba(0,0,0,0.36);
        }

        .brand-title { font-weight:700; font-size:16px; color:#fff; line-height:1.05; }
        .brand-sub { font-size:13px; color: rgba(255,255,255,0.88); }
        .brand-desc { margin:0; color: rgba(255,255,255,0.96); font-size:14px; line-height:1.34; }

        .cta-row { display:flex; gap:8px; align-items:center; margin-top:6px; }
        .cta-btn {
          display:inline-flex;
          align-items:center;
          gap:8px;
          padding: 8px 12px;
          border-radius: 10px;
          font-weight:700;
          text-decoration:none;
          color: var(--dark);
          background: linear-gradient(180deg, rgba(var(--gold-rgb),0.98), rgba(255,242,200,0.94));
          border: 1px solid rgba(0,0,0,0.06);
          box-shadow: 0 12px 40px rgba(var(--gold-rgb),0.12);
        }
        .cta-btn:hover { transform: translateY(-3px); }

        .links {
          background: rgba(255,255,255,0.98);
          border-radius: 12px;
          padding: 12px;
          box-shadow: 0 8px 22px rgba(0,0,0,0.08);
          color: var(--dark);
          border: 1px solid rgba(0,0,0,0.04);
        }
        .links h4 { margin:0 0 8px 0; font-size:15px; font-weight:700; }
        .links nav { display:flex; flex-direction:column; gap:8px; }
        .links a { color: var(--dark); text-decoration:none; font-size:14px; word-break:break-word; }
        .links a:hover { color: rgba(var(--gold-rgb),0.98); transform: translateX(6px); }

        .social {
          background: rgba(255,255,255,0.98);
          border-radius: 12px;
          padding: 12px;
          box-shadow: 0 8px 22px rgba(0,0,0,0.08);
          color: var(--dark);
          display:flex;
          flex-direction:column;
          gap:6px;
          border: 1px solid rgba(0,0,0,0.04);
        }
        .social-head { margin:0 0 6px 0; font-size:15px; font-weight:700; }
        .social-row { display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
        .social-btn {
          width:44px; height:44px; border-radius:999px; display:grid; place-items:center;
          background: rgba(0,0,0,0.65); color: #fff; text-decoration:none; border:none;
        }
        .social-btn:hover { transform: translateY(-4px); box-shadow: 0 14px 36px rgba(var(--gold-rgb),0.12); background: linear-gradient(180deg, rgba(var(--gold-rgb),0.98), rgba(255,242,180,0.92)); color: #0b0b0b; }

        .premium { font-weight:700; padding:8px 10px; border-radius:10px; background:#fff; color:#0b0b0b; display:inline-block; font-size:13px; }
        .copyright { font-size:13px; color: rgba(0,0,0,0.7); margin-top:6px; }

        .bottom-row {
          max-width: var(--max-width);
          margin: 18px auto 0;
          display:flex;
          justify-content: space-between;
          gap:12px;
          align-items:center;
          flex-wrap:wrap;
          box-sizing: border-box;
          padding: 8px 0 12px 0;
        }
        .bottom-links { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
        .bottom-links a { text-decoration:none; color: rgba(0,0,0,0.78); background: rgba(255,255,255,0.9); padding:6px 10px; border-radius:8px; border:1px solid rgba(0,0,0,0.04); }

        @media (max-width: 660px) {
          .logo-tile { width: 64px; height: 64px; }
          .sc-inner { grid-template-columns: 1fr; gap: 12px; }
          .bottom-row { flex-direction: column; align-items:center; gap:10px; padding-bottom: 18px; }
        }

        /* Respect reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .sc-footer { transition: none !important; }
          .cta-btn, .social-btn { transition: none !important; }
        }

        /* Smooth transform when we need to lift (but skip heavy motion if user prefers reduced motion) */
        .sc-footer {
          transition: transform 320ms cubic-bezier(.2,.9,.25,1);
        }
        @media (prefers-reduced-motion: reduce) {
          .sc-footer { transition: none; }
        }
      `}</style>

      <div className="footer-bg" role="presentation">
        <motion.div className="sc-inner" {...(motionProps as any)}>
          {/* Brand */}
          <div className="panel brand" role="region" aria-label="About The Story Club">
            <div className="brand-top">
              <Link href="/" legacyBehavior>
                <a aria-label="Home — The Story Club" style={{ display: "inline-block", textDecoration: "none" }}>
                  <div className="logo-tile" aria-hidden>
                    <div style={{ width: 56, height: 56, position: "relative" }}>
                      <Image
                        src="/logo.png"
                        alt="The Story Club logo"
                        fill
                        sizes="56px"
                        style={{ objectFit: "contain", filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.6))" }}
                        onError={(e) => {
                          const el = e.currentTarget as HTMLImageElement;
                          if (el) el.style.display = "none";
                        }}
                      />
                    </div>
                  </div>
                </a>
              </Link>

              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 4, minWidth: 0 }}>
                <div className="brand-title">The Story Club</div>
                <div className="brand-sub">Interactive stories • cinematic exports</div>
              </div>
            </div>

            <p className="brand-desc">Ignite your imagination — craft interactive stories with images, sound, and cinematic exports. Built for creators who value beauty and speed.</p>

            <div className="cta-row">
              <Link href="/dashboard/new-story" legacyBehavior>
                <a className="cta-btn" aria-label="Start a new story">
                  <span>Start a Story</span>
                  <span aria-hidden><MiniFlame size={12} /></span>
                </a>
              </Link>
            </div>
          </div>

          {/* Links */}
          <div className="panel links" aria-label="Quick links">
            <h4>Navigation</h4>
            <nav aria-label="Footer navigation">
              <Link href="/stories" legacyBehavior><a>Stories</a></Link>
              <Link href="/dashboard/new-story" legacyBehavior><a>New Story</a></Link>
              <Link href="/how-to-use" legacyBehavior><a>How to Use</a></Link>
              <Link href="/pricing" legacyBehavior><a>Pricing</a></Link>
              <Link href="/dashboard" legacyBehavior><a>Dashboard</a></Link>
              <Link href="/about" legacyBehavior><a>About</a></Link>
              <Link href="/contact" legacyBehavior><a>Contact</a></Link>
            </nav>
          </div>

          {/* Social */}
          <div className="panel social" aria-label="Connect with The Story Club">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <h4 className="social-head">Connect With Us</h4>
                <div className="social-row">
                  <a className="social-btn" href="#" aria-label="Facebook" title="Facebook"><Facebook size={16} /></a>
                  <a className="social-btn" href="#" aria-label="Twitter" title="Twitter"><Twitter size={16} /></a>
                  <a className="social-btn" href="#" aria-label="Instagram" title="Instagram"><Instagram size={16} /></a>
                  <a className="social-btn" href="mailto:hello@thestoryclub.example" aria-label="Email" title="Email"><Mail size={14} /></a>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 140 }}>
                <div className="premium" aria-hidden>Premium</div>
                <div className="copyright" style={{ textAlign: "right" }}>
                  © {year} The Story Club. All rights reserved.
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom row with safe padding */}
        <div className="bottom-row" role="contentinfo" aria-label="Footer links and credits">
          <div className="bottom-links">
            <Link href="/terms" legacyBehavior><a>Terms</a></Link>
            <Link href="/privacy" legacyBehavior><a>Privacy</a></Link>
            <Link href="/cookies" legacyBehavior><a>Cookies</a></Link>
          </div>

          <div style={{ color: "rgba(0,0,0,0.72)", fontSize: 13 }}>Made for creators</div>
        </div>
      </div>
    </footer>
  );
}
